import { ChevronUp, ChevronDown, Settings, FileText, BookOpen, HelpCircle, LifeBuoy, LogOut } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSidebar } from "@/components/ui/sidebar";
import { useLogout } from "@/hooks/useLogout";
import { ACCOUNT_ROUTES, PUBLIC_ROUTES } from "@/routes/routes";

interface SidebarProfileFooterProps {
  fullName: string;
  email: string;
  avatarUrl?: string | null;
}

export function SidebarProfileFooter({ fullName, email, avatarUrl }: SidebarProfileFooterProps) {
  const { logout } = useLogout();
  const { open: sidebarOpen } = useSidebar();
  const [open, setOpen] = useState(false);

  // Generate initials from full name
  const userInitials = fullName
    ? fullName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : email?.[0]?.toUpperCase() || "U";

  const handleSignOut = async () => {
    await logout("/login");
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button 
          className="w-full rounded-2xl border bg-background px-3 py-2.5 shadow-sm transition hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`User menu for ${fullName}`}
        >
          <div className="flex items-center gap-3">
            <Avatar className="h-9 w-9 flex-shrink-0">
              <AvatarImage src={avatarUrl || undefined} alt={fullName} />
              <AvatarFallback>{userInitials}</AvatarFallback>
            </Avatar>
            {sidebarOpen && (
              <>
                <div className="min-w-0 flex-1 text-left">
                  <div className="truncate text-sm font-semibold">{fullName}</div>
                  <div className="truncate text-xs text-muted-foreground">{email}</div>
                </div>
                {open ? (
                  <ChevronUp className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                )}
              </>
            )}
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" className="w-72">
        {/* Group 1: Settings & Account */}
        <DropdownMenuItem asChild className="py-2">
          <Link to={ACCOUNT_ROUTES.settings} className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            <span>Settings</span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Group 2: Help & Support */}
        <DropdownMenuItem asChild className="py-2">
          <Link to={PUBLIC_ROUTES.privacyPolicy} className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <span>Privacy Policy</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="py-2">
          <Link to={PUBLIC_ROUTES.terms} className="flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            <span>Terms of Service</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="py-2">
          <Link to={PUBLIC_ROUTES.faq} className="flex items-center gap-2">
            <HelpCircle className="h-4 w-4" />
            <span>Frequently Asked Questions</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="py-2">
          <Link to={PUBLIC_ROUTES.support} className="flex items-center gap-2">
            <LifeBuoy className="h-4 w-4" />
            <span>Contact Support</span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Sign Out */}
        <DropdownMenuItem onSelect={handleSignOut} className="py-2 text-destructive focus:text-destructive">
          <LogOut className="h-4 w-4" />
          <span>Sign out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
